import { createFileRoute, Outlet, redirect, Link, useRouterState } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { LayoutDashboard, Users, Wallet, Image as ImageIcon, Gamepad2, Bell, Settings, LogOut, Trophy, Menu, ArrowDownCircle, ArrowUpCircle, Ticket, Send, AlertTriangle, ShieldCheck, Database, BarChart3, UserCog, Activity, Gauge, CreditCard } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";

export const Route = createFileRoute("/admin")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/auth" });
    const { data: role } = await supabase.from("user_roles").select("role").eq("user_id", data.user.id).in("role", ["admin", "sub_admin"]).maybeSingle();
    if (!role) throw redirect({ to: "/" });
    if (role.role === "sub_admin" && location.pathname === "/admin") {
      throw redirect({ to: "/admin/users" });
    }
    return { user: data.user, role: role.role as "admin" | "sub_admin" };
  },
  component: AdminLayout,
});

// Sub-admins may only access these exact paths.
const SUB_ADMIN_ALLOWED = new Set<string>(["/admin/users", "/admin/deposits", "/admin/results"]);

type NavItem = { to: string; label: string; icon: any; exact?: boolean; adminOnly?: boolean; subAdmin?: boolean };

const navItems: NavItem[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, adminOnly: true },
  { to: "/admin/users", label: "Users", icon: Users, subAdmin: true },
  { to: "/admin/deposits", label: "Manual Deposits", icon: ArrowDownCircle, subAdmin: true },
  { to: "/admin/automatic-payments", label: "Automatic Payments", icon: CreditCard, adminOnly: true },

  { to: "/admin/results", label: "Results", icon: Trophy, subAdmin: true },

  { to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowUpCircle, adminOnly: true },
  { to: "/admin/transactions", label: "Transactions", icon: Wallet, adminOnly: true },
  { to: "/admin/games", label: "Games", icon: Gamepad2, adminOnly: true },
  { to: "/admin/bets", label: "Bets", icon: Ticket, adminOnly: true },
  { to: "/admin/queue", label: "Bet Queue", icon: Activity, adminOnly: true },

  { to: "/admin/reports", label: "User Reports", icon: BarChart3, adminOnly: true },
  { to: "/admin/banners", label: "Banners", icon: ImageIcon, adminOnly: true },
  { to: "/admin/notices", label: "Notices", icon: Bell, adminOnly: true },
  { to: "/admin/notifications", label: "Notifications", icon: Send, adminOnly: true },
  { to: "/admin/errors", label: "App Errors", icon: AlertTriangle, adminOnly: true },
  { to: "/admin/diagnostics", label: "Diagnostics & Performance", icon: Gauge, adminOnly: true },

  { to: "/admin/audit", label: "Audit / Ledger", icon: ShieldCheck, adminOnly: true },
  { to: "/admin/backups", label: "Backup & Restore", icon: Database, adminOnly: true },
  { to: "/admin/sub-admins", label: "Sub Admins", icon: UserCog, adminOnly: true },
  { to: "/admin/telegram", label: "Telegram", icon: Send, adminOnly: true },
  { to: "/admin/settings", label: "Settings", icon: Settings, adminOnly: true },
];

function AdminLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { role } = Route.useRouteContext();
  const isSubAdmin = role === "sub_admin";
  const [open, setOpen] = useState(false);

  async function signOut() {
    await supabase.auth.signOut();
    toast.success("Signed out");
    window.location.href = "/auth";
  }

  // Sub-admin trying to access a restricted page
  if (isSubAdmin && !SUB_ADMIN_ALLOWED.has(pathname)) {
    return (
      <div className="grid min-h-screen place-items-center p-6 text-center">
        <div>
          <p className="text-lg font-black">Access denied</p>
          <p className="mt-1 text-sm text-muted-foreground">Sub Admins can only access Users, Manual Deposits and Results.</p>
          <Link to="/admin/users" className="mt-3 inline-block rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Go to Users</Link>
        </div>
      </div>
    );
  }

  const visibleNav = navItems.filter((it) => (isSubAdmin ? it.subAdmin === true : true));


  return (
    <div className="flex min-h-screen bg-muted/30">
      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 w-64 gradient-primary text-primary-foreground transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full",
      )}>
        <div className="flex h-full flex-col p-4">
          <div className="mb-6 flex items-center gap-2 px-2 shrink-0">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-white/15 text-lg">👑</div>
            <div>
              <p className="text-sm font-black uppercase leading-tight">Brothers Club</p>
              <p className="text-[10px] uppercase tracking-widest text-white/70">{isSubAdmin ? "Sub Admin" : "Admin Panel"}</p>
            </div>
          </div>
          <nav className="space-y-1 overflow-y-auto">
            {visibleNav.map((it) => {
              const active = it.exact ? pathname === it.to : pathname.startsWith(it.to);
              const Icon = it.icon;
              return (
                <Link
                  key={it.to} to={it.to as any} onClick={() => setOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors",
                    active ? "bg-white text-primary" : "text-white/85 hover:bg-white/10",
                  )}
                >
                  <Icon className="h-4 w-4" /> {it.label}
                </Link>
              );
            })}
            <div className="pt-4 pb-2">
              <Link to="/" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-white/85 hover:bg-white/10">
                <LayoutDashboard className="h-4 w-4" /> Back to App
              </Link>
              <button onClick={signOut} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-white/85 hover:bg-white/10">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </nav>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col min-w-0 lg:ml-64 overflow-x-hidden">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b bg-card px-3 py-3 lg:px-6">
          <button onClick={() => setOpen(!open)} className="rounded-lg p-1.5 hover:bg-muted lg:hidden"><Menu className="h-5 w-5" /></button>
          <h2 className="text-base font-bold">Admin</h2>
        </header>
        <main className="admin-scope flex-1 p-3 sm:p-4 lg:p-6 min-w-0">
          <AppErrorBoundary scope="admin">
            <Outlet />
          </AppErrorBoundary>
        </main>
      </div>
    </div>
  );
}
